import React, { useState, useEffect } from 'react';
import {
    Box,
    Drawer,
    List,
    ListItem,
    ListItemButton,
    ListItemText,
    Typography,
    IconButton,
    Divider,
    Paper,
    Collapse,
    InputBase,
    useMediaQuery,
    Button,
    Popover,
    MenuItem,
    ListItemIcon,
} from '@mui/material';
import { MoreVert, ExpandLess, ExpandMore, History, Search, DeleteOutline } from '@mui/icons-material';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import BorderColorOutlinedIcon from '@mui/icons-material/BorderColorOutlined';
import './sidebar.less';
import HomeImage from '../../../assets/images/house.png';
import SidebarCollapse from '../../../assets/images/collapse.png';
import MailOutlineIcon from '@mui/icons-material/MailOutline';
import { useNavigate } from 'react-router-dom';
import { deleteChatSession } from '../../../api/sidebarAPI';
import moment from 'moment';
import { CircularProgress } from '@mui/material';
import RenameDialog from '../../molecules/askAsper/modals/renameDialog';
import { UserContext } from 'contexts/ConfigContext';
import { getConfig } from 'utils/helperMethods';

const truncateTitle = (title, maxLength = 20) => {
    if (!title) {
        return '';
    }
    if (title.length > maxLength) {
        return title.substring(0, maxLength) + '...';
    }
    return title;
};

const ChatHistoryItem = ({
    title,
    thread_id,
    handleHistoryClick,
    selectedThreadID,
    onDeleteThread,
    onRenameThread,
}) => {
    const [anchorEl, setAnchorEl] = useState(null);
    const [renameDialogOpen, setRenameDialogOpen] = useState(false);
    const [isDeleting, setIsDeleting] = useState(false);

    let style =
        thread_id === selectedThreadID
            ? {
                  mt: '-5px',
                  background: '#ffffff',
              }
            : { mt: '-5px' };

    const handleMenuOpen = (event) => {
        setAnchorEl(event.currentTarget);
    };

    const handleMenuClose = () => {
        setAnchorEl(null);
    };

    const open = Boolean(anchorEl);
    const id = open ? 'chat-menu-popover' : undefined;

    const handleRenameClick = () => {
        setRenameDialogOpen(true);
        handleMenuClose();
    };

    const handleRenameConfirm = (newTitle) => {
        onRenameThread(thread_id, newTitle);
        setRenameDialogOpen(false);
    };

    const handleRenameCancel = () => {
        setRenameDialogOpen(false);
    };

    const handleDeleteClick = (event) => {
        event.stopPropagation();
        setIsDeleting(true);
        if (onDeleteThread) {
            onDeleteThread(thread_id, () => {
                setIsDeleting(false);
                handleMenuClose();
            });
        } else {
            setIsDeleting(false);
            handleMenuClose();
        }
    };

    return (
        <ListItem disablePadding sx={style}>
            <ListItemButton sx={{ py: 0.5, pr: 0, justifyContent: 'space-between' }}>
                <ListItemText
                    primary={
                        <Box
                            sx={{
                                display: 'flex',
                                alignItems: 'center',
                                paddingRight: 1,
                                flexGrow: 1,
                                cursor: 'pointer',
                            }}
                            onClick={() => {
                                handleHistoryClick(thread_id);
                            }}
                        >
                            <Typography
                                variant="body2"
                                color="text.primary"
                                sx={{ fontWeight: 400, fontSize: '14px', flexGrow: 1 }}
                            >
                                {truncateTitle(title)}
                            </Typography>
                        </Box>
                    }
                />
                {thread_id !== selectedThreadID && (
                    <IconButton size="small" onClick={handleMenuOpen} disabled={isDeleting}>
                        <MoreVert fontSize="small" />
                    </IconButton>
                )}
            </ListItemButton>
            {thread_id !== selectedThreadID && (
                <>
                    <Popover
                        id={id}
                        open={open}
                        anchorEl={anchorEl}
                        onClose={handleMenuClose}
                        anchorOrigin={{
                            vertical: 'bottom',
                            horizontal: 'right',
                        }}
                        transformOrigin={{
                            vertical: 'top',
                            horizontal: 'right',
                        }}
                    >
                        <MenuItem onClick={handleRenameClick} disabled={isDeleting}>
                            <ListItemIcon>
                                {/* {isDeleting ? <CircularProgress size={20} /> :  */}
                                <EditOutlinedIcon fontSize="small" />
                                {/* } */}
                            </ListItemIcon>
                            <Typography variant="body2">Rename</Typography>
                        </MenuItem>
                        <MenuItem onClick={handleDeleteClick} disabled={isDeleting}>
                            <ListItemIcon>
                                {isDeleting ? <CircularProgress size={20} /> : <DeleteOutline fontSize="small" />}
                            </ListItemIcon>
                            <Typography variant="body2">Delete</Typography>
                        </MenuItem>
                    </Popover>
                    <RenameDialog
                        open={renameDialogOpen}
                        initialTitle={title}
                        thread_id={thread_id}
                        onConfirm={handleRenameConfirm}
                        onCancel={handleRenameCancel}
                    />
                </>
            )}
        </ListItem>
    );
};

const ChatHistorySection = ({
    title,
    items,
    searchTerm,
    showChat,
    handleHistoryClick,
    selectedThreadID,
    onDeleteThread,
    onRenameThread,
}) => {
    const filteredItems = searchTerm
        ? items.filter((item) => item.title.toLowerCase().includes(searchTerm.toLowerCase()))
        : items;

    if (searchTerm && filteredItems.length === 0) return null;

    return (
        <>
            <Collapse in={showChat} timeout="auto" unmountOnExit>
                <ListItem sx={{ mt: '1rem' }}>
                    <Typography variant="body1" color="text.secondary" sx={{ fontWeight: 600, fontSize: '14px' }}>
                        {title}
                    </Typography>
                </ListItem>
                <List disablePadding>
                    {filteredItems.map((item, index) => (
                        <ChatHistoryItem
                            key={index}
                            title={item.title}
                            date={title}
                            handleHistoryClick={handleHistoryClick}
                            thread_id={item.thread_id}
                            selectedThreadID={selectedThreadID}
                            onDeleteThread={onDeleteThread}
                            onRenameThread={onRenameThread}
                        />
                    ))}
                </List>
            </Collapse>
        </>
    );
};

const transformChatHistory = (apiResponse) => {
    if (!apiResponse || apiResponse.length === 0) {
        return [];
    }

    const groupedChats = {};
    const now = moment();
    const thisWeekStart = now.clone().startOf('week');
    const thisWeekEnd = now.clone().endOf('week');
    const lastWeekStart = moment().subtract(1, 'week').startOf('week');
    const lastWeekEnd = moment().subtract(1, 'week').endOf('week');

    apiResponse.forEach((chat) => {
        const createdOn = moment(chat.created_on);
        let sectionTitle = '';

        if (createdOn.isSame(now, 'day')) {
            sectionTitle = 'Today';
        } else if (createdOn.isSame(now.clone().subtract(1, 'day'), 'day')) {
            sectionTitle = 'Yesterday';
        } else if (createdOn.isBetween(thisWeekStart, thisWeekEnd, null, '[]')) {
            sectionTitle = 'This Week';
        } else if (createdOn.isBetween(lastWeekStart, lastWeekEnd, null, '[]')) {
            sectionTitle = 'Last Week';
        } else {
            sectionTitle = createdOn.format('MMMM');
        }

        if (!groupedChats[sectionTitle]) {
            groupedChats[sectionTitle] = {
                title: sectionTitle,
                items: [],
            };
        }

        groupedChats[sectionTitle].items.push({ title: chat.thread_name, thread_id: chat.thread_id });
    });

    const sectionsArray = Object.values(groupedChats);

    sectionsArray.sort((a, b) => {
        const order = ['Today', 'Yesterday', 'This Week', 'Last Week'];
        const indexA = order.indexOf(a.title);
        const indexB = order.indexOf(b.title);

        if (indexA !== -1 && indexB !== -1) {
            return indexA - indexB;
        }
        if (indexA !== -1) {
            return -1;
        }
        if (indexB !== -1) {
            return 1;
        }

        const monthA = moment(a.title, 'MMMM');
        const monthB = moment(b.title, 'MMMM');
        return monthB.valueOf() - monthA.valueOf();
    });

    return sectionsArray;
};

const Sidebar = ({
    toggleSidebar,
    open,
    handleHistoryClick,
    historyData,
    selectedThreadID,
    chatMessages,
    handleStartNewButtonClick,
    isLoadingHistory,
}) => {
    const [searchTerm, setSearchTerm] = useState('');
    const [isSearchBarVisible, setIsSearchBarVisible] = useState(false);
    const [isSearchBarDisabled, setIsSearchBarDisabled] = useState(true);
    const [startNewChatButtonDisabled, setStartNewChatButtonDisabled] = useState(true);
    const [formattedHistoryData, setFormattedHistoryData] = useState([]);
    const isMobile = useMediaQuery('(max-width:768px)');
    const navigate = useNavigate();
    const [threads, setThreads] = useState([]);

    useEffect(() => {
        if (historyData.length > 0) {
            const formattedChatHistory = transformChatHistory(historyData);
            setFormattedHistoryData(formattedChatHistory);
            setThreads(historyData);
            setIsSearchBarDisabled(false);
        } else {
            setFormattedHistoryData([]);
            setThreads([]);
            setIsSearchBarDisabled(true);
        }
    }, [historyData]);

    useEffect(() => {
        if (chatMessages.length > 0) {
            setStartNewChatButtonDisabled(false);
        } else {
            setStartNewChatButtonDisabled(true);
        }
    }, [chatMessages]);

    const handleContactUs = () => {
        const recipient = 'askasper@asper.ai';
        const subject = 'Feedback on ask asper';
        const body = 'I have some feedback...';
        const mailtoLink = `mailto:${recipient}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(
            body,
        )}`;
        window.location.href = mailtoLink;
    };

    const toggleSearchInput = () => {
        setIsSearchBarVisible(!isSearchBarVisible);
    };

    const handleDeleteThread = async (threadIdToDelete, resetDeletingState) => {
        try {
            await deleteChatSession(threadIdToDelete);
            console.log('Chat session deleted successfully:', threadIdToDelete);
            const updatedThreads = threads.filter((thread) => thread.thread_id !== threadIdToDelete);
            setThreads(updatedThreads);
            const formattedChatHistory = transformChatHistory(updatedThreads);
            setFormattedHistoryData(formattedChatHistory);
            if (resetDeletingState) {
                resetDeletingState();
            }
        } catch (error) {
            console.error('Failed to delete chat session:', error);
            if (resetDeletingState) {
                resetDeletingState();
            }
        }
    };

    const handleRenameThread = (threadIdToRename, newThreadName) => {
        console.log('Renaming thread:', threadIdToRename, 'to:', newThreadName);
        const updatedThreads = threads.map((thread) =>
            thread.thread_id === threadIdToRename ? { ...thread, thread_name: newThreadName } : thread,
        );
        setThreads(updatedThreads);
        const formattedChatHistory = transformChatHistory(updatedThreads);
        setFormattedHistoryData(formattedChatHistory);
    };

    function renderSidebarContent() {
        const [openCollapse, setOpenCollapse] = useState(true);

        const handleClick = () => {
            setOpenCollapse(!openCollapse);
        };

        return (
            <>
                <Box sx={{ p: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
                    <IconButton
                        onClick={() => {
                            navigate('/home');
                        }}
                    >
                        <img src={HomeImage} alt="cq-logo" width={19} height={17} />
                    </IconButton>
                    <div className="icons-wrapper-left">
                        <IconButton className="appbar-icons-button" disabled={isSearchBarDisabled}>
                            <Search
                                sx={{
                                    width: '22px',
                                    height: '22px',
                                    fill: isSearchBarDisabled ? '#5D5D5D66' : 'black',
                                }}
                                onClick={toggleSearchInput}
                            />
                        </IconButton>
                        <IconButton
                            className="appbar-icons-button"
                            disabled={startNewChatButtonDisabled}
                            onClick={handleStartNewButtonClick}
                        >
                            <BorderColorOutlinedIcon
                                sx={{
                                    width: '22px',
                                    height: '22px',
                                    fill: startNewChatButtonDisabled ? '#5D5D5D66' : 'black',
                                }}
                            />
                        </IconButton>
                        <IconButton className="appbar-icons-button" onClick={toggleSidebar}>
                            <img src={SidebarCollapse} alt="cq-logo" width={15} height={15} />
                        </IconButton>
                    </div>
                </Box>

                <ListItem
                    button
                    sx={{
                        py: 1,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                    }}
                    onClick={handleClick}
                >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <History fontSize="small" />
                        <Typography variant="subtitle1" sx={{ fontWeight: '600', fontSize: '14px' }}>
                            Chat History
                        </Typography>
                    </Box>
                    <IconButton>{openCollapse ? <ExpandLess /> : <ExpandMore />}</IconButton>
                </ListItem>

                {isSearchBarVisible && (
                    <Box sx={{ px: 2, py: 1 }}>
                        <Box
                            sx={{
                                display: 'flex',
                                alignItems: 'center',
                                backgroundColor: 'rgba(0, 0, 0, 0.05)',
                                borderRadius: '8px',
                                padding: '4px 8px',
                            }}
                        >
                            <Search sx={{ mr: 1, color: 'text.secondary' }} />
                            <InputBase
                                placeholder="Search chats..."
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                                sx={{ flex: 1, fontSize: '0.9rem' }}
                            />
                        </Box>
                    </Box>
                )}
                <Divider />

                <Box sx={{ overflow: 'auto', height: 'calc(100% - 130px)' }}>
                    {isLoadingHistory ? (
                        <div
                            style={{
                                display: 'flex',
                                justifyContent: 'center',
                                alignItems: 'center',
                                height: 'calc(100% - 130px)',
                            }}
                        >
                            <CircularProgress />
                        </div>
                    ) : (
                        <List sx={{ px: 1, pr: 0.5 }}>
                            {formattedHistoryData.map((section, index) => (
                                <ChatHistorySection
                                    key={index}
                                    title={section.title}
                                    items={section.items}
                                    searchTerm={searchTerm}
                                    showChat={openCollapse}
                                    handleHistoryClick={handleHistoryClick}
                                    selectedThreadID={selectedThreadID}
                                    onDeleteThread={handleDeleteThread}
                                    onRenameThread={handleRenameThread}
                                />
                            ))}
                            {formattedHistoryData.length === 0 && !isLoadingHistory && (
                                <Typography
                                    variant="body2"
                                    color="text.secondary"
                                    sx={{ textAlign: 'center', mt: 2, fontSize: '12px' }}
                                >
                                    No chat history available.
                                </Typography>
                            )}
                        </List>
                    )}
                </Box>

                <Divider />
                <Box
                    sx={{
                        padding: '8px 0px',
                        textAlign: 'center',
                        background: '#fff',
                        borderRadius: '16px',
                        marginBottom: '30px',
                        marginTop: '15px',
                    }}
                >
                    <Button
                        startIcon={<MailOutlineIcon />}
                        size="small"
                        sx={{ color: 'black', textTransform: 'none', fontSize: '12px' }}
                        onClick={handleContactUs}
                    >
                        Write to us on &nbsp; <strong>askasper@asper.ai</strong>
                    </Button>
                </Box>
            </>
        );
    }

    return (
        <>
            {isMobile ? (
                <Drawer
                    open={open}
                    onClose={toggleSidebar}
                    className="sidebar-container"
                    sx={{
                        '& .MuiDrawer-paper': {
                            width: '240px',
                            backgroundColor: 'white',
                            height: '100%',
                            zIndex: 1300,
                        },
                    }}
                >
                    {renderSidebarContent()}
                </Drawer>
            ) : (
                <Paper
                    elevation={3}
                    className="sidebar-container"
                    sx={{
                        position: 'absolute',
                        top: '16px',
                        bottom: '16px',
                        width: '240px',
                        zIndex: 100,
                        transform: open ? 'translateX(0)' : 'translateX(-280px)',
                        transition: 'transform 0.3s ease',
                        backgroundColor: 'white',
                    }}
                >
                    {renderSidebarContent()}
                </Paper>
            )}
        </>
    );
};

export default Sidebar;
